import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Heading, Text, Flex, useToast, Select, Input, Switch, Tooltip, Spacer, useDisclosure, Modal, ModalOverlay, ModalCloseButton, ModalContent, ModalHeader, ModalBody, ModalFooter,} from "@chakra-ui/react";
import { MdDelete, MdOutlinePlayArrow, } from "react-icons/md";
import { react_api } from "../../config";
import { CASE_TYPE } from "../../CaseTypeCostant";
import { HTTP_STATUS_CODES } from "../../GrinsCostant";
import logout from "../../services/authService";
import createAxiosInstance from "../../interceptors/useAxiosInterceptors";
import { useTranslation } from "react-i18next";
import { useSession } from "../../SessionContext";
import { useNavigate, useLocation } from "react-router-dom";
import SpinnerXL from "../SpinnerXL";
import { useBreakpointValue } from "@chakra-ui/react";

const getBackgroundColorByVisibility = (visibility) => {
    if (visibility === "private") {
        return "green.400";
    } else if (visibility === "obtained") {
        return "orange.400";
    } else if (visibility === "public") {
        return "yellow.400";
    } else if (visibility === "general") {
        return "blue.400";
    } else {
        return "blue.500";
    }
};
const DatasetItem = ({ id, text, visibility, onSelect, isSelected }) => {
    let buttonColor = isSelected ? "gray.400" : getBackgroundColorByVisibility(visibility);
    return (
        <Button
            h="20px"
            bg={buttonColor}
            borderWidth={1}
            borderColor={"black"}
            onClick={()=> onSelect(text)}
            isDisabled={isSelected} 
            maxW="100%" 
            overflow="hidden" 
            textOverflow="ellipsis" 
            whiteSpace="nowrap"
            display="inline-block"
        >
        {text}
        </Button>
    );
};

const CategoryBox = ({ id, name, color, items, onSelect, selectedDataset, t, searchTerm, onSearchChange, }) => {
    const filteredItems = items.filter((item) =>
        item.table_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return (
        <Box key={id} mt={4} p={3} borderRadius="md" borderWidth={2} borderColor={`${color}.500`}>
            <Heading fontSize="md" color={`${color}.600`} mb={2}>
            {name}
            </Heading>
            <Input
                bg={"white"}
                placeholder={`${t("safeai.datasetBox.searchDataset")}${name}`}
                value={searchTerm}
                onChange={(e) => onSearchChange(id, e.target.value)}
                mb={2}
            />
            {filteredItems.length > 0 ? (
                filteredItems.map(({ table_id, table_name, visibility }) => (
                    <DatasetItem
                        key={table_id}
                        id={{ id: table_name, type: "dataset" }}
                        text={table_name}
                        visibility={visibility}
                        onSelect={onSelect}
                        isSelected={selectedDataset === table_name}
                    />
                ))
            ) : (
            <Text color="gray.500">{t("safeai.datasetBox.noDatasetsAvailable")}</Text>
            )}  
        </Box>
    );
};

function Safeai() {
    const location = useLocation();
    const titleService = location.state?.title;
    const { t } = useTranslation();
    const [catalog, setCatalog] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [columns, setColumns] = useState([]);
    const [gtColumn, setGtColumn] = useState("");
    const [pdColumn, setPdColumn] = useState("");
    const [protectedColumn, setProtectedColumn] = useState("");
    const [mlModel, setMlModel] = useState("");
    const toast = useToast();
    const navigate = useNavigate();
    const { updateSessionData, clearSessionData } = useSession();
    const axiosInstance = createAxiosInstance(async (axiosInstance) => {
        logout(t, clearSessionData, navigate, toast, axiosInstance);
    });
    const [operations, setOperations] = useState([]);
    const operationsSafe = ["Accuracy", "Robustness", "Explainability", "Fairness", "Simplified fairness"];
    const [resultApp, setResultApp] = useState(false);
    const [htmlResult, setHtmlResult] = useState("");
    const [spinnerLoading, setSpinnerLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const richiesta = {
                    BearerToken: sessionStorage.getItem("token"),
                    username: sessionStorage.getItem("username"),
                    caseType: CASE_TYPE.CATALOG_ALL,
                };
                const options = {
                    headers: {
                    "Content-Type": "application/json",
                    }
                };
                const urlFetch = `${react_api}/DragAndDrop/getDatasetsADP`;
                const response = await axiosInstance.post(urlFetch, richiesta, options);
                const result = response.data;
                const tablesInfo = result?.tables_info;

                if ( result == null || (Object.keys(result).length === 0)) {
                    toast({
                        title: t('toastMsg.error'),
                        description: t('messageBE.momentaryCommunicationError'), 
                        status: 'warning',
                        duration: 4000,
                        isClosable: true,
                        position: 'top'
                    });
                    return;  
                }

                if (!Array.isArray(tablesInfo) || tablesInfo.length === 0) {
                    toast({
                        title: t('toastMsg.error'),
                        description: t('toastMsg.momentaryCommunicationError'),
                        status: 'warning',
                        duration: 4000,
                        isClosable: true,
                        position: 'top'
                    });
                    return;
                }
                if (result.status !== HTTP_STATUS_CODES.OK) {
                    toast({
                    title: t('toastMsg.error'),
                    description: t(`messageBE.${result.message}`),
                    position: "top",
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                    });
                }
                if (result.status === HTTP_STATUS_CODES.OK) {
                    setCatalog(result.tables_info);
                }
            } catch (error) {
            toast({
                title: t('toastMsg.callError'),
                description: String(error),
                position: "top",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            }
        };
        fetchCatalog();
    }, [toast]);

    const fetchColumns = async (tableId) => {
        try {
            const urlFetch = `${react_api}/DragAndDrop/getDatasetColADP/${tableId}`;
            const response = await axiosInstance.get(urlFetch);
            const result = response?.data;

            if ( result == null || (Object.keys(result).length === 0)) {
                toast({
                    title: t('toastMsg.error'),
                    description: t('messageBE.momentaryCommunicationError'), 
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                    position: 'top'
                });
                return;  
            }

            return result;

        } catch (error) {
            toast({
                title: t('toastMsg.callError'),
                description: String(error),
                position: "top",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
        }
    };
    
    const sendDataToBackend = async () => {
        try {
            setSpinnerLoading(true);
            const payload = {
                BearerToken: sessionStorage.getItem("token"),
                username: sessionStorage.getItem("username"),
                service_name: "safeai",
                dataset: selectedDataset,
                operation: operations,
                gtColumn: gtColumn,
                pdColumn: pdColumn,
                protectedColumns : protectedColumn,
                // ...(operations.includes("Simplified fairness") && { protectedColumn }),
                    // ...(showMlModalSelect && { mlModel }),
            };
            const urlFetch = `${react_api}/linkInternalServicesHTMLReceivedADP`;
            const response = await axiosInstance.post(urlFetch, payload);
            const result = response?.data;

            if ( result == null || (Object.keys(result).length === 0)) {
                toast({
                    title: t('toastMsg.error'),
                    description: t('messageBE.momentaryCommunicationError'), 
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                    position: 'top'
                });
                return;  
            }
            if (result.status === undefined) {
                setSpinnerLoading(false);
                toast({
                    title: t("toastMsg.error"),
                    description: "Invalid token",
                    position: "top",
                    status: "warning",
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            if (result.status !== HTTP_STATUS_CODES.OK) {
                setSpinnerLoading(false);
                toast({
                title: t("toastMsg.error"),
                description: t(`messageBE.${result.message}`),
                position: "top",
                status: "error",
                duration: 4000,
                isClosable: true,
                });
            }
            if (result.status === HTTP_STATUS_CODES.OK) {
                setSpinnerLoading(false);
                setHtmlResult(result.htmlPage);
                setResultApp(true);
            }
        } catch (error) {
            setSpinnerLoading(false);
            toast({
                title: t("toastMsg.callError"),
                description: String(error.message),
                position: "top",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
        }
    };
    
    const [searchTerms, setSearchTerms] = useState({});
    
    const handleSearchChange = (categoryId, value) => {
        setSearchTerms((prev) => ({ ...prev, [categoryId]: value }));
    };

    const categories = [
        { id: 1, name: "Catalog General", type: "catalogo_generale", color: "blue" },
        { id: 2, name: "Catalog Results", type: "catalogo_risultati", color: "green" },
        { id: 3, name: "Catalog Processing", type: "catalogo_elaborazioni", color: "orange" },
        { id: 4, name: "Catalog Staging", type: "catalogo_staging", color: "purple" },
    ];

    const clearAllData = () => {
        setSearchTerms({});
        setSelectedDataset(null);
        setOperations([]);
        setColumns([]);
        setGtColumn(""); 
        setPdColumn("");
        setProtectedColumn(""); 
        setMlModel("");
        clearResult();
    };

    const handleDatasetSelect = async (dataset) => {
        clearAllData();
        setSelectedDataset(dataset);
        const table = catalog.find(item => item.table_name === dataset);
        if (!table) return;
        try {
            const res = await fetchColumns(table.table_id);
            const tableInfo = Array.isArray(res.tableInfo) ? res.tableInfo : [];
            const allCols = tableInfo.map(col => ({
                name: col.columnName,
                index: col.index
            }));
            const columnsRemoved = ['metadata_vd', 'metadata_vtd', 'metadata_ud'];
            const filtered = allCols
                .filter(col => !columnsRemoved.includes(col.name))
                .sort((a, b) => a.index - b.index)
                .map(col => col.name);
            setColumns(filtered);
        } catch (error) {
            console.error('Error fetchColumns:', error);
        }
    };
    

    const toggleOperation = (op) => {
        setOperations((prev) => {
            const newOps = prev.includes(op) ? prev.filter((x) => x !== op) : [...prev, op];
            if (prev.includes(op) && op === "Simplified fairness") {
                setProtectedColumn("");
            }
            if (prev.includes(op) && newOps.length === 0) {
                setGtColumn("");
                setPdColumn("");
                setProtectedColumn("");
            }
            return newOps;
        });
    };

    // const showMlModalSelect = operations.some(o => o !== "Accuracy");
    const showProtectedSelect = operations.includes("Simplified fairness");

    const isStartDisabled = () => {
        if (operations.length === 0) return true;
        if (!gtColumn || !pdColumn) return true;
        // if (showMlModalSelect && !mlModel) return true;
        if (showProtectedSelect && !protectedColumn) return true;
        return false;
    }; 
    
    const clearResult = () =>{
        setResultApp(false);
        setHtmlResult("");
    };

    const handleConfirm = () => {
        clearAllData();
        clearResult();
        onClose();
    };

    // useEffect(() => {
    //     if (isStartDisabled()) {
    //         clearResult();
    //     }
    // }, [
    //     selectedDataset,  
    //     operations, 
    // ]);

    const responsiveHeight = useBreakpointValue({
        base: "auto",
        md: "80vh",
        lg: "calc(100vh - 70px)" 
    });

    return (
        <Box position="relative" height="100%" width="100%" p={2} bg="#f6f3ec">
            {spinnerLoading && (
                <SpinnerXL />
            )}
            <Flex Flex mb={2} align="center" gap={2} pr={3}>
                <Heading color={"rgba(11,78,90,255)"} mr={10}>{titleService}</Heading>
                <Spacer />
                <Button size="sm" variant="outline" _hover={{bg: "red.500", color: "white",}} colorScheme="red" onClick={() => onOpen()}>{t('safeai.btnClearData')} <MdDelete size={18} /></Button>
                <Button size="sm" variant="outline" _hover={{bg: "green.500", color: "white",}} colorScheme="green" onClick={sendDataToBackend} isDisabled={isStartDisabled()}>{t('safeai.btnStart')} <MdOutlinePlayArrow size={18} /></Button>
            </Flex>
            <Flex height={responsiveHeight} gap={2} align="flex-start" overflowX="auto">
                <>
                    <Box borderWidth={2} overflowY="scroll" borderColor="rgba(23, 141, 161, 1)" borderRadius={5} height="100%" width="30%" p={3}>
                        <Box justifyContent="space-between">
                            <Heading fontSize="lg" color="rgba(11,78,90,255)" fontWeight="bold">Dataset</Heading>
                            <Box w="100%" justifyContent="space-between">
                                {t('safeai.datasetBox.legend')}:
                                <Badge ml={2} colorScheme="green" variant="solid">Private</Badge>
                                <Badge ml={2} colorScheme="yellow" variant="solid">Public</Badge>
                                <Badge ml={2} colorScheme="orange" variant="solid">Obtained</Badge>
                                <Badge ml={2} colorScheme="blue" variant="solid">General</Badge>
                            </Box>
                        </Box>

                        <Box justifyContent="space-between" mt={"1%"}>
                            <Heading fontSize="lg" color="rgba(11,78,90,255)" fontWeight="bold">Catalog</Heading>
                            <Box w="100%" justifyContent="space-between">
                                {t('safeai.datasetBox.legend')}:
                                <Badge ml={2} colorScheme="blue" variant="solid">General</Badge>
                                <Badge ml={2} colorScheme="green" variant="solid">Results</Badge>
                                <Badge ml={2} colorScheme="orange" variant="solid">Processing</Badge>
                                <Badge ml={2} colorScheme="purple" variant="solid">Staging</Badge>
                            </Box>
                        </Box>
                        {categories.map(({ id, name, color }) => {
                            const filteredCatalog = catalog.filter(item => Number(item.cataloguetype_id) === id);

                            return (
                                <CategoryBox
                                    key={id}
                                    id={id}
                                    name={name}
                                    color={color}
                                    items={filteredCatalog}
                                    onSelect={handleDatasetSelect}
                                    selectedDataset={selectedDataset}
                                    searchTerm={searchTerms[id] || ""}
                                    onSearchChange={handleSearchChange}
                                    t={t}
                                />
                            );
                        })}
                    </Box>
                    {selectedDataset && (
                        <Box borderColor="rgba(23, 141, 161, 1)" borderRadius={5} borderWidth={2} height="100%" width="20%" overflowY="auto" p={4} >    
                            <Box mb={4}>
                                <Text fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">Dataset :</Text>
                                <Tooltip label={selectedDataset} hasArrow>
                                    <Badge colorScheme="teal" fontWeight="normal" variant="solid" px={1} py={0} maxW="100%" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" display="inline-block" textTransform="none" fontSize="md">
                                        {selectedDataset}
                                    </Badge>
                                </Tooltip>
                            </Box>
                            <Box mb={4}>
                                <Text mb={1} fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">{t('safeai.operationsBox.selectSafeMetric')} :</Text>
                                {operationsSafe.map((op) => (
                                    <Flex key={op} align="center" textOverflow="ellipsis" maxW="100%">
                                        <Text m={1} maxW="100%" overflow="hidden" textOverflow="ellipsis" whiteSpace="normal" display="inline-block">{op}</Text>
                                        <Switch colorScheme="teal" disabled={op !== "Accuracy" && op !== "Simplified fairness" } isChecked={operations.includes(op)}  onChange={() => toggleOperation(op)}/>
                                    </Flex>
                                ))}
                            </Box>            
                            {operations.length > 0 && (
                                <>
                                    <Box mb={4}>
                                        <Text mb={2} fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">{t('safeai.operationsBox.selectTestGround‐TruthColumn')}:</Text>
                                        <Select bg="white" placeholder={t('safeai.operationsBox.selectGround‐truthColumn')} value={gtColumn} onChange={e => setGtColumn(e.target.value)}>
                                            {columns.filter(c => c !== pdColumn && c !== protectedColumn).map(c => <option key={c} value={c}>{c}</option>)}
                                        </Select>
                                    </Box>
                                    <Box mb={4}>
                                        <Text mb={2} fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">{t('safeai.operationsBox.selectTestPredictionColumn')}:</Text>
                                        <Select bg="white" placeholder={t('safeai.operationsBox.selectPredictionColumn')} value={pdColumn} onChange={e => setPdColumn(e.target.value)}>
                                            {columns.filter(c => c !== gtColumn && c !== protectedColumn).map(c => <option key={c} value={c}>{c}</option>)}
                                        </Select>
                                    </Box>
                                    {operations.includes("Simplified fairness") &&(
                                        <Box mb={4}>
                                            <Text mb={2} fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">{t('safeai.operationsBox.selectProtectedColumn')}:</Text>
                                            <Select bg="white" placeholder={t('safeai.operationsBox.selectProtectedColumn')} value={protectedColumn} onChange={e => setProtectedColumn(e.target.value)}>
                                                {columns.filter(c => c !== gtColumn && c !== pdColumn).map(c => <option key={c} value={c}>{c}</option>)}
                                            </Select>
                                        </Box>
                                    )}
                                    {/* {showMlModalSelect && (
                                        <Box mb={4}>
                                            <Text mb={2} fontSize="md" color="rgba(11,78,90,255)" fontWeight="semibold">{t('safeai.operationsBox.selectMlModel')}:</Text>
                                            <Select bg="white" placeholder= {t('safeai.operationsBox.selectMlModel')} value={mlModel} onChange={e => setMlModel(e.target.value)}>
                                                {columns.filter(c => c !== gtColumn && c !== pdColumn).map(c => <option key={c} value={c}>{c}</option>)}
                                            </Select>
                                        </Box>
                                    )} */}
                                </>
                            )}
                        </Box>
                    )}
                    {resultApp && (
                        <Box borderColor="rgba(23, 141, 161, 1)" borderRadius={5} borderWidth={2} height="100%" width="50%">
                            <iframe
                                srcDoc={htmlResult}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="S.A.F.E. - AI Results"
                            /> 
                        </Box>
                    )}
                </>
            </Flex>
            <Modal isOpen={isOpen} onClose={() => { onClose(); }} closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('safeai.modal.confirmationCancellation')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {t('safeai.modal.infoConfirmationCancellation')}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="green" mr={3} onClick={handleConfirm}>
                        {t('safeai.modal.yes')}
                        </Button>
                        <Button colorScheme="red" onClick={() => { onClose(); }}>
                        No
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default Safeai;